// This file configures the initialization of Sentry and PostHog on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
// https://posthog.com/docs/libraries/next-js

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/results/:id\",\"regex\":\"^/results/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/:slug\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/companies/:companyId\",\"regex\":\"^/admin/companies/([^/]+)$\",\"paramNames\":[\"companyId\"],\"hasOptionalPrefix\":false},{\"path\":\"/admin/users/:userId\",\"regex\":\"^/admin/users/([^/]+)$\",\"paramNames\":[\"userId\"],\"hasOptionalPrefix\":false},{\"path\":\"/blog/:slug\",\"regex\":\"^/blog/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/visualize-wheels-on/:vehicleId\",\"regex\":\"^/visualize-wheels-on/([^/]+)$\",\"paramNames\":[\"vehicleId\"],\"hasOptionalPrefix\":false},{\"path\":\"/wheel-brands/:brand\",\"regex\":\"^/wheel-brands/([^/]+)$\",\"paramNames\":[\"brand\"],\"hasOptionalPrefix\":false},{\"path\":\"/wheel-guide/:make\",\"regex\":\"^/wheel-guide/([^/]+)$\",\"paramNames\":[\"make\"],\"hasOptionalPrefix\":false},{\"path\":\"/wheel-guide/:make/:model\",\"regex\":\"^/wheel-guide/([^/]+)/([^/]+)$\",\"paramNames\":[\"make\",\"model\"],\"hasOptionalPrefix\":false},{\"path\":\"/wheel-guide/:make/:model/:slug\",\"regex\":\"^/wheel-guide/([^/]+)/([^/]+)/([^/]+)$\",\"paramNames\":[\"make\",\"model\",\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/app\"},{\"path\":\"/contact\"},{\"path\":\"/credits\"},{\"path\":\"/privacy\"},{\"path\":\"/terms\"},{\"path\":\"/admin\"},{\"path\":\"/admin/campaigns\"},{\"path\":\"/admin/companies\"},{\"path\":\"/admin/email\"},{\"path\":\"/admin/gallery\"},{\"path\":\"/admin/model-test\"},{\"path\":\"/before-and-after-wheels\"},{\"path\":\"/blog\"},{\"path\":\"/custom-wheel-upload\"},{\"path\":\"/for-dealers\"},{\"path\":\"/gallery\"},{\"path\":\"/photo-tips\"},{\"path\":\"/pricing\"},{\"path\":\"/see-wheels-on-my-car\"},{\"path\":\"/truck-wheel-visualizer\"},{\"path\":\"/virtual-wheel-fitting\"},{\"path\":\"/wheel-brands\"},{\"path\":\"/wheel-fitment-preview\"},{\"path\":\"/wheel-guide\"},{\"path\":\"/wheel-simulator\"}],\"isrRoutes\":[\"/:slug\",\"/blog/:slug\",\"/visualize-wheels-on/:vehicleId\",\"/wheel-brands/:brand\",\"/wheel-guide/:make\",\"/wheel-guide/:make/:model\",\"/wheel-guide/:make/:model/:slug\"]}";globalThis["_sentryNextJsVersion"] = "16.1.1";import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

// Initialize PostHog for client-side analytics
// Using instrumentation-client.ts is the recommended approach for Next.js 15.3+
// Only initialize in production to avoid polluting analytics with dev data
// NOTE: gated behind NEXT_PUBLIC_POSTHOG_ENABLED to allow disabling all events.
if (process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_POSTHOG_ENABLED === 'true') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    // Use reverse proxy to avoid tracking blockers
    api_host: "/ingest",
    // UI host for accessing PostHog dashboards
    ui_host: "https://eu.posthog.com",
    // Use new defaults for better pageview/pageleave handling
    defaults: '2025-11-30',
    // Enable automatic exception capture
    capture_exceptions: true,
    // Enable autocapture for click events, form submissions, etc.
    autocapture: true,
    // Only create person profiles for identified users
    person_profiles: 'identified_only',
    // Enable session recording
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
    // Callback to opt out of tracking on admin pages on initial load
    loaded: (posthog) => {
      // Check if we're on an excluded page on initial load and opt out if so
      // Route changes are handled by PostHogAdminExclusion component
      if (typeof window !== 'undefined') {
        const isExcludedRoute = window.location.pathname.startsWith('/admin');
        if (isExcludedRoute) {
          posthog.opt_out_capturing();
          if (typeof posthog.stopSessionRecording === "function") {
            posthog.stopSessionRecording();
          }
        }
      }
    },
  });
}

Sentry.init({
  dsn: "https://9f8a7e97b2645c2462489ee193b48b64@o4510696582283264.ingest.us.sentry.io/4510696583331840",

  // Add optional integrations for additional features
  integrations: [Sentry.replayIntegration()],

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: 1,
  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Define how likely Replay events are sampled.
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  // Enable sending user PII (Personally Identifiable Information)
  // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  // Filter out expected network errors
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Filter out network errors that are expected (CORS, network failures, etc.)
    if (error instanceof TypeError) {
      const errorMessage = error.message?.toLowerCase() || "";
      const errorString = String(error).toLowerCase();

      // Common network error patterns
      const networkErrorPatterns = [
        "load failed",
        "failed to fetch",
        "networkerror",
        "network error",
        "network request failed",
        "cors",
        "cross-origin",
        "connection refused",
        "connection reset",
        "timeout",
        "aborted",
      ];

      // Also check the error message format: "Load failed (domain.com)"
      const loadFailedPattern = /load failed\s*\(/i;

      const isNetworkError = 
        networkErrorPatterns.some(
          (pattern) => errorMessage.includes(pattern) || errorString.includes(pattern)
        ) ||
        loadFailedPattern.test(errorMessage) ||
        loadFailedPattern.test(errorString);

      if (isNetworkError) {
        // Check if it's a fetch to an external domain (not our API)
        const stack = error.stack || "";
        const isExternalFetch = 
          stack.includes("thewheelvisualizer.com") ||
          stack.includes("@better-fetch") ||
          event.request?.url?.includes("thewheelvisualizer.com");

        // Only filter if it's clearly an external fetch that failed
        // Keep internal API errors for debugging
        if (isExternalFetch && !event.request?.url?.startsWith("/api/")) {
          // Return null to drop the event, or return a modified event with lower severity
          return null;
        }
      }
    }

    const exceptionValues = event.exception?.values ?? [];
    const isDomRemoveChildError = exceptionValues.some((value) => {
      const type = value?.type || "";
      const message = value?.value || "";
      return (
        type === "DOMException" &&
        message.includes("NotFoundError") &&
        message.includes("removeChild")
      );
    });

    if (isDomRemoveChildError && typeof window !== "undefined") {
      event.tags = {
        ...event.tags,
        dom_exception: "removeChild_notfound",
        path: window.location.pathname,
      };
      event.extra = {
        ...event.extra,
        visibilityState: document.visibilityState,
        readyState: document.readyState,
      };
      // Benign DOM mutation race (often from browser translation/extensions).
      // Drop to avoid false reliability signals.
      return null;
    }

    return event;
  },

  // Handle unhandled promise rejections
  ignoreErrors: [
    // Network errors that are expected
    /^Load failed/,
    /Load failed \(/,
    /^Failed to fetch/,
    /^NetworkError/,
    /^Network request failed/,
    /^TypeError: Load failed/,
    /TypeError.*Load failed/,
  ],
});

// Handle unhandled promise rejections globally
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    
    // Check if it's a network error we want to ignore
    if (error instanceof TypeError) {
      const errorMessage = String(error).toLowerCase();
      const isNetworkError = 
        errorMessage.includes("load failed") ||
        errorMessage.includes("failed to fetch") ||
        errorMessage.includes("networkerror");

      if (isNetworkError) {
        // Check if it's related to external fetches
        const stack = error.stack || "";
        if (stack.includes("thewheelvisualizer.com") || stack.includes("@better-fetch")) {
          // Suppress the error - it's an expected network failure
          event.preventDefault();
          console.warn("Suppressed network error:", error);
          return;
        }
      }
    }

    // For other errors, let Sentry handle them
    Sentry.captureException(error);
  });
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
